import { useEffect, useMemo, useState } from 'react';
import { Check, Copy, Folder } from 'react-feather';

import { Button } from '~ui/base/button';
import { Card } from '~ui/base/card';
import { Skeleton } from '~ui/base/skeleton';
import { Typography } from '~ui/base/typography';
import { cn } from '~lib/utils/style';

import { useEpflFolders } from '~modules/epfl/hooks/use-epfl-folders';
import {
  buildMountSnippet,
  MOUNT_TOOLS,
  type MountTool,
} from '~modules/epfl/lib/mount-snippet';
import type { EpflFolder } from '~lib/storage/epfl';

function EpflSection() {
  const { status, folders, error, refresh } = useEpflFolders();

  return (
    <section className="flex flex-col gap-y-6">
      <div className="flex flex-col gap-y-3">
        <Typography variant="headline-md" as="h2">
          EPFL
        </Typography>
        <Typography
          variant="body-lg-extralight"
          as="p"
          className="max-w-2xl text-muted-foreground"
        >
          Large folders of data stored under the{' '}
          <span className="font-mono">epfl</span> container. These are far too
          big to download file-by-file — pick a folder and mount it directly
          with <span className="font-medium text-foreground">rclone</span> or{' '}
          <span className="font-medium text-foreground">blobfuse2</span> using
          the snippet below.
        </Typography>
      </div>

      {(status === 'idle' || status === 'loading') && <EpflSkeleton />}
      {status === 'error' && error && (
        <EpflError error={error} onRetry={refresh} />
      )}
      {status === 'success' && folders.length === 0 && <EpflEmpty />}
      {status === 'success' && folders.length > 0 && (
        <EpflMounter folders={folders} />
      )}
    </section>
  );
}

interface EpflMounterProps {
  folders: EpflFolder[];
}

function EpflMounter({ folders }: EpflMounterProps) {
  const [selectedName, setSelectedName] = useState(folders[0].name);
  const [tool, setTool] = useState<MountTool>('rclone');

  // Keep the selection valid if the folder list changes underneath us.
  const selected = useMemo(
    () => folders.find((f) => f.name === selectedName) ?? folders[0],
    [folders, selectedName],
  );

  const snippet = useMemo(() => {
    const [container] = selected.path.split('/');
    return buildMountSnippet(tool, container, selected.name);
  }, [tool, selected]);

  return (
    <Card ornament="outline" className="grid gap-6 p-6 lg:grid-cols-[18rem_minmax(0,1fr)]">
      <div className="flex flex-col gap-y-3">
        <Typography variant="overline" as="span" className="text-muted-foreground">
          {folders.length.toLocaleString()}{' '}
          {folders.length === 1 ? 'folder' : 'folders'}
        </Typography>
        <ul className="flex flex-col gap-y-1">
          {folders.map((folder) => (
            <li key={folder.name}>
              <FolderButton
                folder={folder}
                selected={folder.name === selected.name}
                onSelect={() => setSelectedName(folder.name)}
              />
            </li>
          ))}
        </ul>
      </div>

      <div className="flex flex-col gap-y-4">
        <div className="flex items-center justify-between gap-x-4">
          <Typography variant="subtitle-sm" as="span" className="font-mono">
            {selected.path}
          </Typography>
          <ToolTabs tool={tool} onChange={setTool} />
        </div>
        <CodeBlock code={snippet} />
      </div>
    </Card>
  );
}

interface FolderButtonProps {
  folder: EpflFolder;
  selected: boolean;
  onSelect: () => void;
}

function FolderButton({ folder, selected, onSelect }: FolderButtonProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        'flex w-full items-center gap-x-2.5 rounded-lg px-3 py-2 text-left transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        selected
          ? 'bg-accent-container text-accent-container-foreground'
          : 'text-muted-foreground hover:bg-card-highlight hover:text-foreground',
      )}
    >
      <Folder
        size={16}
        aria-hidden
        className={cn('shrink-0', selected ? 'text-primary' : 'opacity-70')}
      />
      <Typography variant="body-md" as="span" className="truncate font-mono">
        {folder.name}
      </Typography>
    </button>
  );
}

interface ToolTabsProps {
  tool: MountTool;
  onChange: (tool: MountTool) => void;
}

function ToolTabs({ tool, onChange }: ToolTabsProps) {
  return (
    <div
      role="tablist"
      aria-label="Mount tool"
      className="inline-flex shrink-0 rounded-full border border-border p-0.5"
    >
      {MOUNT_TOOLS.map(({ id, label }) => {
        const active = id === tool;
        return (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(id)}
            className={cn(
              'rounded-full px-3 py-1 font-mono text-xs transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
              active
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

interface CodeBlockProps {
  code: string;
}

function CodeBlock({ code }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
    } catch {
      // Clipboard can be blocked (insecure context / permissions); the snippet
      // is still selectable by hand, so there's nothing useful to surface.
    }
  };

  return (
    <div className="relative">
      <pre className="overflow-x-auto rounded-xl border border-border bg-card-highlight/60 p-4 pr-14">
        <code className="font-mono text-xs leading-relaxed text-foreground/90">
          {code}
        </code>
      </pre>
      <Button
        variant="tertiary"
        size="icon-sm"
        onClick={handleCopy}
        aria-label={copied ? 'Copied' : 'Copy to clipboard'}
        className="absolute right-3 top-3 bg-card"
      >
        {copied ? (
          <Check size={14} aria-hidden className="text-primary" />
        ) : (
          <Copy size={14} aria-hidden />
        )}
      </Button>
    </div>
  );
}

function EpflSkeleton() {
  return (
    <Card
      ornament="outline"
      className="grid gap-6 p-6 lg:grid-cols-[18rem_minmax(0,1fr)]"
    >
      <div className="flex flex-col gap-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-9 w-full" />
        ))}
      </div>
      <Skeleton className="h-48 w-full" />
    </Card>
  );
}

function EpflEmpty() {
  return (
    <Card ornament="outline" className="p-8">
      <Typography variant="body-md" as="p" className="text-muted-foreground">
        No folders found in the <span className="font-mono">epfl</span>{' '}
        container.
      </Typography>
    </Card>
  );
}

interface EpflErrorProps {
  error: Error;
  onRetry: () => void;
}

function EpflError({ error, onRetry }: EpflErrorProps) {
  return (
    <div
      className={cn(
        'rounded-[1.25rem] border border-destructive/30',
        'bg-destructive-container p-8 text-destructive-container-foreground',
      )}
    >
      <Typography variant="subtitle-sm" as="h3">
        Couldn&apos;t load EPFL folders
      </Typography>
      <Typography variant="body-md" as="p" className="mt-3">
        {error.message}
      </Typography>
      <Button variant="secondary" size="md" className="mt-5" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}

EpflSection.displayName = 'EpflSection';

export { EpflSection };
